import { FC, Fragment, useState } from 'react';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Table,
	Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useForm } from '@/libs/antd/form';
import {
	addCCAOfficeObservationInventory,
	deleteCCAOfficeObservationInventory,
	updateCCAOfficeObservationInventory,
} from '@/libs/fleet-api/services/visit-reports';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import rule from '@/libs/antd/rules';
import { useMutation } from '@tanstack/react-query';
import { OfficeObservationInventory } from '@/libs/fleet-api/services/types';
import { useSelectInventoryItemType } from '@/libs/fleet-api/hooks/master-data';
import { MasterData } from '@/libs/fleet-api/services/master-data';
import { DefaultOptionType } from 'antd/es/select';
import { useCCAVisitReportVerificationPage } from './VisitReportVerificationPage';
import { useModals } from '@/components/modals/ModalProvider';

type VisitReporInventoryItem = OfficeObservationInventory<'payload'> &
	Pick<OfficeObservationInventory, 'type'>;

type VisitReporInventoryForm = {
	input?: VisitReporInventoryItem;
	lists: VisitReporInventoryItem[];
};

const CCAVisitReportInventory: FC = () => {
	const { officeObservation, surveyInformation } =
		useCCAVisitReportVerificationPage();
	const { warehouse } = officeObservation ?? {};
	const { form, fieldName } = useForm<VisitReporInventoryForm>();
	const [modal, setModal] = useState<
		{ type: 'add' } | { type: 'edit'; index: number } | null
	>(null);

	const addCCAOfficeObservationInventoryMutation = useMutation({
		mutationFn: addCCAOfficeObservationInventory,
	});

	const updateCCAOfficeObservationInventoryMutation = useMutation({
		mutationFn: updateCCAOfficeObservationInventory,
	});

	const deleteCCAOfficeObservationInventoryMutation = useMutation({
		mutationFn: deleteCCAOfficeObservationInventory,
	});

	const selectInventoryItemType = useSelectInventoryItemType();
	const getSelectInventoryItemType = () => {
		const inventoryItemType = form.getFieldValue(
			fieldName(['input', 'type']),
		) as MasterData | null;

		if (!inventoryItemType || selectInventoryItemType.options?.length) {
			return selectInventoryItemType.options;
		}

		return [
			{
				value: inventoryItemType.id,
				label: inventoryItemType.value,
			},
		] as DefaultOptionType[];
	};

	const { modal: modalConfirm } = useModals();

	return (
		<Form.Provider>
			<Typography.Title level={3}>Kondisi Barang Persediaan</Typography.Title>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={
					{
						lists:
							warehouse?.inventories?.map((record) => {
								return {
									...record,
									typeId: record.type.id,
								};
							}) ?? [],
					} as VisitReporInventoryForm
				}
				onFinish={async ({ input, lists }) => {
					if (modal?.type === 'edit') {
						await updateCCAOfficeObservationInventoryMutation.mutateAsync({
							...input!,
							surveyId: surveyInformation!.id!,
						});
						form.setFieldsValue({
							lists: lists.map((list, index) => {
								return index === modal.index ? input! : list;
							}),
						});
					} else {
						const res =
							await addCCAOfficeObservationInventoryMutation.mutateAsync({
								...input!,
								surveyId: surveyInformation!.id!,
							});
						form.setFieldsValue({
							lists: [...lists, { ...input!, id: res.data.id }],
						});
					}
					setModal(null);
				}}
				style={{ marginBottom: 30 }}
			>
				<Form.Item
					name={fieldName(['input'])}
					noStyle
				/>
				<Form.Item
					name={fieldName(['lists'])}
					noStyle
				/>
				<Form.Item shouldUpdate>
					{({ getFieldsValue, setFieldValue }) => {
						const values = getFieldsValue() as VisitReporInventoryForm;
						const dataSource =
							values?.lists?.map((list, key?) => {
								return { ...list, key };
							}) ?? [];

						return (
							<Fragment>
								<Row style={{ display: 'flex', justifyContent: 'end' }}>
									<Button
										style={{
											color: '#1557BC',
											borderRadius: 50,
											fontSize: 12,
											height: 24,
											borderColor: '#1557BC',
											fontWeight: 700,
											marginBottom: 20,
										}}
										loading={
											addCCAOfficeObservationInventoryMutation.isPending ||
											updateCCAOfficeObservationInventoryMutation.isPending
										}
										onClick={() => setModal({ type: 'add' })}
										icon={'+'}
									>
										Add Another
									</Button>
								</Row>
								<Table
									bordered
									pagination={false}
									dataSource={dataSource}
									columns={[
										{
											title: (
												<span
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													Action
												</span>
											),
											render: (_, record, index) => {
												return (
													<Row
														gutter={[40, 20]}
														style={{
															display: 'flex',
															flexWrap: 'nowrap',
															justifyContent: 'center',
															paddingLeft: 20,
															paddingRight: 20,
														}}
													>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	setFieldValue(fieldName(['input']), record);
																	setModal({
																		type: 'edit',
																		index,
																	});
																}}
															>
																<EditOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={async (e) => {
																	e.preventDefault();
																	const confirmDelete =
																		await modalConfirm.confirm({
																			title:
																				'Anda yakin akan menghapus data ini?',
																		});

																	if (confirmDelete) {
																		await deleteCCAOfficeObservationInventoryMutation.mutateAsync(
																			record.id!,
																		);
																		form.setFieldsValue({
																			lists: values.lists.filter(
																				(_, listIndex) => listIndex !== index,
																			),
																		});
																	}
																}}
															>
																<DeleteOutlined />
															</a>
														</div>
													</Row>
												);
											},
											width: 1,
										},
										{
											title: 'Jenis Barang Persediaan',
											render: (_, record) => record.type?.value ?? '-',
										},
										{
											title: 'Detail Barang Persediaan',
											render: (_, record) => record.name ?? '-',
										},
										{
											title: 'Jumlah Persediaan Saat Kunjungan Usaha',
											render: (_, record) => record.quantity ?? '-',
										},
									]}
								/>
								<FleetModal
									open={Boolean(modal)}
									width={940}
									closeIcon={false}
									keyboard={false}
									maskClosable={false}
									buttonPosition="end"
									afterClose={() => {
										form.resetFields([fieldName('input')]);
									}}
									onCancel={() => {
										setModal(null);
									}}
									onOk={() => {
										form.submit();
									}}
								>
									<FleetTitle>Add Kondisi Barang Persediaan</FleetTitle>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'id'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'surveyId'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'typeId'])}
												label="Jenis Barang Persediaan"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Select
													{...selectInventoryItemType}
													options={getSelectInventoryItemType()}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'name'])}
												label="Detail Barang Persediaan"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'quantity'])}
												label="Jumlah Persediaan"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<InputNumber min={0} />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
								</FleetModal>
							</Fragment>
						);
					}}
				</Form.Item>
			</Form>
		</Form.Provider>
	);
};

export default CCAVisitReportInventory;
